import { VStack, FormControl } from '@cad-arc';
import { BoloAutocompleteView, EventAutocompleteView } from '@mark43/cad-api';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { FormInput } from '../../forms/integrated-form-controls/form-input';
import { EventAutocomplete } from '../../forms/inputs/event-autocomplete';
import { SelectedEntityCard } from './selected-entity-card';
import { BoloAutocomplete } from '../../forms/inputs/bolo-autocomplete';

export type AssociateEntityFormDisplayType = {
    entityId: number;
    associatedEntities: EventAutocompleteView[] | BoloAutocompleteView[];
    reason?: string;
};

export function AssociateEntityForm({ entityType }: { entityType: 'BOLO' | 'EVENT' }) {
    const { control } = useFormContext<AssociateEntityFormDisplayType>();
    const associatingBolos = entityType === 'BOLO';

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'associatedEntities',
    });

    const searchLabel = associatingBolos ? 'Search by Bolo #, Bolo Type' : 'Search by Event #, CFS';

    return (
        <>
            <FormControl label="Search" helpText={searchLabel}>
                {associatingBolos ? (
                    <BoloAutocomplete
                        defaultValue={null}
                        onChange={(value) => {
                            if (
                                value &&
                                !fields.some(
                                    (field) =>
                                        (field as BoloAutocompleteView).boloId === value.data.boloId
                                )
                            ) {
                                append(value.data);
                            }
                        }}
                    />
                ) : (
                    <EventAutocomplete
                        defaultValue={null}
                        data-testid="associate-event-search"
                        onChange={(value) => {
                            if (
                                value &&
                                !fields.some(
                                    (field) =>
                                        (field as EventAutocompleteView).agencyEventId ===
                                        value.data.agencyEventId
                                )
                            ) {
                                append(value.data);
                            }
                        }}
                    />
                )}
            </FormControl>
            <div
                style={{ maxHeight: '60vh', overflow: 'auto', paddingBottom: 10 }}
                data-testid="associate-entity-selected-entities-list"
            >
                <Controller
                    control={control}
                    name="associatedEntities"
                    rules={{
                        required: `At least one associated ${
                            associatingBolos ? 'BOLO' : 'Event'
                        } is required`,
                    }}
                    render={({ fieldState: { error } }) => {
                        return (
                            <FormControl errorMessage={error?.message} isInvalid={!!error?.message}>
                                <VStack
                                    gap={1}
                                    alignItems="stretch"
                                    marginTop="2"
                                    marginBottom="2"
                                    flexGrow={1}
                                >
                                    {fields.map((field, index) => {
                                        return (
                                            <div key={field.id}>
                                                <SelectedEntityCard
                                                    field={field}
                                                    onRemove={() => remove(index)}
                                                />
                                            </div>
                                        );
                                    })}
                                </VStack>
                            </FormControl>
                        );
                    }}
                />
            </div>
            <FormInput
                control={control}
                name="reason"
                label="Reason for association"
                rules={{
                    maxLength: {
                        message: 'Comments cannot exceed 255 characters in length.',
                        value: 255,
                    },
                }}
            />
        </>
    );
}
